<template>
  <div class="acb-root">
    <div ref="listEl" class="acb-list">
      <div v-if="!messages.length" class="acb-empty">
        <i class="fas fa-robot acb-empty-ico"></i>
        <p class="acb-empty-intro">{{ $t('Assistant.EmptyHint.Intro') }}</p>
        <ul class="acb-empty-list">
          <li>{{ $t('Assistant.EmptyHint.List1') }}</li>
          <li>{{ $t('Assistant.EmptyHint.List2') }}</li>
          <li>{{ $t('Assistant.EmptyHint.List3') }}</li>
          <li>{{ $t('Assistant.EmptyHint.List4') }}</li>
        </ul>
        <div class="acb-suggestions">
          <button class="pbtn pbtn--outline pbtn--sm" @click="send($t('Assistant.Suggest.Purchases'))">
            {{ $t('Assistant.Suggest.Purchases') }}
          </button>
        </div>
      </div>

      <div
        v-for="m in messages"
        :key="m.id"
        class="acb-msg"
        :class="m.role === 'user' ? 'acb-msg--user' : 'acb-msg--assistant'"
      >
        <div class="acb-msg-col">
          <template v-for="(b, i) in m.blocks" :key="i">
            <div v-if="b.type === 'text'" class="acb-bubble">
              <span class="acb-bubble-text acb-md" v-html="renderMarkdown(b.text)"></span>
              <span v-if="m.streaming && i === m.blocks.length - 1" class="acb-caret">▍</span>
            </div>
            <AssistantWidgetBlock
              v-else-if="b.type === 'widget'"
              class="acb-widget"
              :widget-type="b.widgetType"
              :widget-id="b.widgetId"
              :data="b.data"
            />
          </template>
        </div>
      </div>

      <!-- Thinking indicator: shown from send until the first token arrives -->
      <div v-if="showThinking" class="acb-msg acb-msg--assistant">
        <div class="acb-bubble acb-bubble--thinking">
          <span class="acb-think-dot"></span><span class="acb-think-dot"></span><span class="acb-think-dot"></span>
        </div>
      </div>

      <!-- Tool activity chip -->
      <div v-if="toolActivity" class="acb-tool">
        <i class="fas fa-gear fa-spin"></i>
        {{ $t('Assistant.ToolActivity', { tool: toolLabel(toolActivity) }) }}
      </div>

      <!-- Choice buttons -->
      <div v-if="choices" class="acb-choices">
        <div class="acb-choices-prompt">{{ choices.prompt }}</div>
        <div class="acb-choices-btns">
          <button
            v-for="c in choices.choices"
            :key="c.value"
            class="pbtn pbtn--primary pbtn--sm"
            @click="pick(c)"
          >{{ c.label }}</button>
        </div>
      </div>

      <div v-if="error" class="acb-error">
        <i class="fas fa-triangle-exclamation"></i> {{ errorText }}
      </div>
    </div>

    <!-- Usage / disclaimer strip -->
    <div class="acb-footnote">
      <span v-if="usageLine" class="acb-usage"><i class="fas fa-circle-info"></i>{{ usageLine }}</span>
      <span class="acb-disclaimer">{{ $t('Assistant.Disclaimer') }}</span>
    </div>

    <!-- Composer -->
    <form class="acb-composer" @submit.prevent="onSubmit">
      <textarea
        ref="textareaEl"
        v-model="draft"
        class="acb-input"
        rows="2"
        :placeholder="$t('Assistant.InputPlaceholder')"
        :disabled="busy"
        @input="autoGrow"
        @keydown.enter.exact.prevent="onSubmit"
      ></textarea>
      <button v-if="busy" type="button" class="pbtn pbtn--outline" @click="cancel">
        <i class="fas fa-stop me-1"></i>{{ $t('Assistant.Stop') }}
      </button>
      <button v-else type="submit" class="pbtn pbtn--primary" :disabled="!draft.trim()">
        <i class="fas fa-paper-plane me-1"></i>{{ $t('Assistant.Send') }}
      </button>
    </form>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { useAssistantChat } from './useAssistantChat'
import AssistantWidgetBlock from './widgets/AssistantWidgetBlock.vue'

marked.setOptions({ breaks: true })

export default {
  name: 'AssistantChatBody',
  components: { AssistantWidgetBlock },
  setup() {
    const chat = useAssistantChat()
    return { ...chat }
  },
  data() {
    return { draft: '' }
  },
  computed: {
    errorText() {
      if (this.error === 'ws_error') return this.$t('Assistant.Error.Connection')
      return this.error
    },
    showThinking() {
      if (!this.busy) return false
      const last = this.messages[this.messages.length - 1]
      return !(last && last.streaming)
    },
    usageLine() {
      const b = this.aiBilling
      if (!b) return ''
      if (b.access === 'trial' && b.trial_credits_quota > 0) {
        return this.$t('Assistant.Usage.Trial', { used: b.trial_credits_used, quota: b.trial_credits_quota })
      }
      if ((b.access === 'premium' || b.access === 'legacy') && b.monthly_credits_included > 0) {
        return this.$t('Assistant.Usage.Monthly', { used: b.monthly_credits_used, quota: b.monthly_credits_included })
      }
      if (b.access === 'free' && b.free_credits_quota > 0) {
        return this.$t('Assistant.Usage.Free', { used: b.free_credits_used, quota: b.free_credits_quota })
      }
      return ''
    },
  },
  watch: {
    // Deep: a widget block pushed mid-message changes message content without changing
    // messages.length, so a shallow watch would miss the scroll.
    messages: { deep: true, handler() { this.scrollDown() } },
    toolActivity() { this.scrollDown() },
    choices() { this.scrollDown() },
    showThinking() { this.scrollDown() },
  },
  mounted() {
    this.connect()
    this.loadConversationList()
    this.loadBilling()
  },
  methods: {
    onSubmit() {
      const t = this.draft.trim()
      if (!t) return
      this.draft = ''
      nextTick(() => this.autoGrow())
      this.send(t)
    },
    autoGrow() {
      const el = this.$refs.textareaEl
      if (!el) return
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 160)}px`
    },
    send(text) {
      this.sendMessage(text)
    },
    pick(c) {
      this.selectChoice(c.value, c.label)
    },
    toolLabel(tool) {
      const key = `Assistant.Tools.${tool}`
      const translated = this.$t(key)
      return translated === key ? tool : translated
    },
    scrollDown() {
      nextTick(() => {
        const el = this.$refs.listEl
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    renderMarkdown(text) {
      // The model reads data other people wrote (subscriber messages, automations); an <img>
      // it is talked into emitting would leak data to an external URL on render. No images/forms.
      return DOMPurify.sanitize(marked.parse(text || ''), {
        FORBID_TAGS: ['img', 'picture', 'source', 'video', 'audio', 'iframe', 'form', 'input', 'style'],
        FORBID_ATTR: ['style', 'srcset'],
      })
    },
  },
}
</script>

<style scoped>
.acb-root { flex: 1; display: flex; flex-direction: column; min-height: 0; min-width: 0; }
.acb-list { flex: 1; overflow-y: auto; padding: 18px; display: flex; flex-direction: column; gap: 12px; }

.acb-empty { margin: auto; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 0 16px; }
.acb-empty-ico { font-size: 34px; color: var(--ui-text-4); }
.acb-empty-intro { font: 500 13px/1.5 'Open Sans', sans-serif; color: var(--ui-text-3); margin: 0; max-width: 340px; }
.acb-empty-list {
  list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px;
  text-align: left; max-width: 320px; width: 100%;
}
.acb-empty-list li {
  position: relative; padding-left: 18px; font: 600 12.5px/1.4 'Open Sans', sans-serif; color: var(--ui-text-2);
}
.acb-empty-list li::before {
  content: ''; position: absolute; left: 0; top: 7px; width: 6px; height: 6px; border-radius: 50%;
  background: var(--c-primary-500);
}
.acb-suggestions { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-top: 4px; }

.acb-msg { display: flex; }
.acb-msg--user { justify-content: flex-end; }
.acb-msg--assistant { justify-content: flex-start; }
.acb-msg-col { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; max-width: 78%; }
.acb-msg--assistant .acb-msg-col { align-items: flex-start; max-width: 92%; }
.acb-bubble {
  padding: 10px 13px; border-radius: var(--radius-lg);
  font: 500 13.5px/1.5 'Open Sans', sans-serif; word-break: break-word;
}
.acb-bubble-text { display: block; }
.acb-md :deep(p) { margin: 0 0 8px; }
.acb-md :deep(p:last-child) { margin-bottom: 0; }
.acb-md :deep(ul), .acb-md :deep(ol) { margin: 0 0 8px; padding-left: 20px; }
.acb-md :deep(li) { margin-bottom: 2px; }
.acb-md :deep(h1), .acb-md :deep(h2), .acb-md :deep(h3), .acb-md :deep(h4) {
  margin: 12px 0 6px; font-weight: 700; line-height: 1.3; font-family: 'Open Sans', sans-serif; color: inherit;
}
.acb-md :deep(h1) { font-size: 16px; }
.acb-md :deep(h2) { font-size: 15px; }
.acb-md :deep(h3) { font-size: 14px; }
.acb-md :deep(h4) { font-size: 13.5px; }
.acb-md :deep(h1:first-child), .acb-md :deep(h2:first-child),
.acb-md :deep(h3:first-child), .acb-md :deep(h4:first-child) { margin-top: 0; }
.acb-md :deep(code) {
  font-family: 'SFMono-Regular', Consolas, monospace; font-size: 12.5px;
  background: var(--ui-bg-1, rgba(0, 0, 0, 0.08)); border-radius: 4px; padding: 1px 5px;
}
.acb-md :deep(pre) {
  background: var(--ui-bg-1, rgba(0, 0, 0, 0.08)); border-radius: var(--radius-md, 8px);
  padding: 10px 12px; overflow-x: auto; margin: 0 0 8px;
}
.acb-md :deep(pre code) { background: none; padding: 0; }
.acb-md :deep(a) { color: inherit; text-decoration: underline; }
.acb-md :deep(blockquote) {
  margin: 0 0 8px; padding-left: 10px; border-left: 3px solid var(--ui-border); opacity: 0.85;
}
.acb-md :deep(table) { border-collapse: collapse; margin: 0 0 8px; }
.acb-md :deep(th), .acb-md :deep(td) { border: 1px solid var(--ui-border); padding: 4px 8px; }
.acb-widget { width: 100%; }
.acb-msg--user .acb-bubble { background: var(--c-primary-500); color: #fff; border-bottom-right-radius: var(--radius-sm); }
.acb-msg--assistant .acb-bubble { background: var(--ui-bg-sunken); color: var(--ui-text-1); border-bottom-left-radius: var(--radius-sm); }
.acb-caret { animation: acb-blink 1s steps(2) infinite; color: var(--ui-text-3); }
@keyframes acb-blink { 0%, 50% { opacity: 1; } 50.01%, 100% { opacity: 0; } }

.acb-bubble--thinking { display: flex; align-items: center; gap: 4px; padding: 13px 14px; }
.acb-think-dot {
  width: 6px; height: 6px; border-radius: 50%; background: var(--ui-text-4);
  animation: acb-think-bounce 1.1s ease-in-out infinite;
}
.acb-think-dot:nth-child(2) { animation-delay: .15s; }
.acb-think-dot:nth-child(3) { animation-delay: .3s; }
@keyframes acb-think-bounce {
  0%, 80%, 100% { opacity: .35; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
}

.acb-tool { display: inline-flex; align-items: center; gap: 8px; font: 600 12px 'Open Sans', sans-serif; color: var(--ui-text-3); }

.acb-choices { display: flex; flex-direction: column; gap: 8px; }
.acb-choices-prompt { font: 600 12.5px 'Open Sans', sans-serif; color: var(--ui-text-2); }
.acb-choices-btns { display: flex; flex-wrap: wrap; gap: 8px; }

.acb-error { display: inline-flex; align-items: center; gap: 8px; font: 600 12.5px 'Open Sans', sans-serif; color: var(--c-danger-fg, #c0392b); }

.acb-footnote {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  padding: 6px 14px 0; border-top: 1px solid var(--ui-border); background: var(--ui-bg-card);
  font: 500 11px 'Open Sans', sans-serif; color: var(--ui-text-4);
}
.acb-usage { display: inline-flex; align-items: center; gap: 5px; }
.acb-usage i { font-size: 10px; }
.acb-disclaimer { margin-left: auto; }

.acb-composer { display: flex; align-items: flex-end; gap: 8px; padding: 10px 12px 12px; background: var(--ui-bg-card); }
.acb-input {
  flex: 1; min-height: 64px; max-height: 220px; padding: 10px 14px; border: 1px solid var(--ui-border);
  border-radius: var(--radius-lg); background: var(--ui-bg-sunken); color: var(--ui-text-1);
  font: 500 13.5px/1.4 'Open Sans', sans-serif; outline: none; resize: vertical; overflow-y: auto;
}
.acb-input:focus { border-color: var(--c-primary-500); }
.acb-input::placeholder { color: var(--ui-text-4); }
</style>
